/**
 * InvoiceDetailScreen — read-only invoice surface with line items, payments
 * and a header bar offering Return + Print + Back.
 *
 *   ┌─────────── Header (#, customer, totals chips) ───────────┐
 *   │                                              [Return] [Print]
 *   ├──────────── Line items table ────────────────┬──── Side ─┤
 *   │  Item · Qty · Unit · Discount · Tax · Total  │ Customer  │
 *   │                                              │ Payments  │
 *   └──────────────────────────────────────────────┴───────────┘
 */

import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useEffect, useMemo, useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, RotateCcw, Printer, FileDown, FileText, Receipt, ShieldCheck, AlertTriangle, Loader2, Ban } from 'lucide-react';
import { database } from '@/db';
import { Invoice, LineItem, Payment, VOIDED_INVOICE_STATUSES } from '@/db/models/Billing';
import { Product } from '@/db/models/Inventory';
import { Customer } from '@/db/models/Customers';
import { useObservable } from '@/hooks/useObservable';
import { useEInvoice } from '@/hooks/useEInvoice';
import { useInvoicePrint } from '@/printing';
import { notifyError, notifySuccess } from '@/store/useToastStore';
import { DISPATCH_FIELDS, parseTerms } from '@/billing/invoiceExtras';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import { usePermissions, BILLING_CREDIT_NOTE, BILLING_VOID } from '@/rbac';
import { useAppStore } from '@/store/useAppStore';
import { voidInvoice } from '@/billing/invoiceVoid';

const TYPE_LABEL: Record<string, string> = {
  SALE: 'Sale',
  CREDIT_NOTE: 'Credit note',
};

export function InvoiceDetailScreen() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const { isPrinting, print80mm, printSystem } = useInvoicePrint();
  // A print that fails used to do nothing visible at all.
  const printWith = (run: (id: string) => Promise<{ ok: boolean; message?: string }>) => async () => {
    if (!inv) return;
    const res = await run(inv.id);
    if (!res.ok) notifyError(res.message || 'Could not print.');
  };
  const { can } = usePermissions();
  const userId = useAppStore((s) => s.currentUser?.id);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidError, setVoidError] = useState<string | null>(null);
  useErrorPopup(voidError, () => setVoidError(null));

  // ── Invoice header — single-shot load is enough; line items / payments
  //    are observed reactively below so any updates flow through naturally.
  const [headerInvoice, setHeaderInvoice] = useState<Invoice | null>(null);
  useEffect(() => {
    if (!invoiceId) return;
    let cancelled = false;
    (async () => {
      try {
        const inv = await database.get<Invoice>('invoices').find(invoiceId);
        if (!cancelled) setHeaderInvoice(inv);
      } catch {
        if (!cancelled) setHeaderInvoice(null);
      }
    })();
    return () => { cancelled = true; };
  }, [invoiceId]);

  const inv = headerInvoice;

  // ── Line items (reactive) ──
  const lineItems = useObservable<LineItem[]>(
    () => {
      if (!invoiceId) return null;
      return database
        .get<LineItem>('line_items')
        .query(
          Q.where('invoice_id', invoiceId),
          Q.where('is_deleted', false),
        )
        .observe();
    },
    [invoiceId],
    [],
  );

  // ── Payments (reactive) ──
  const payments = useObservable<Payment[]>(
    () => {
      if (!invoiceId) return null;
      return database
        .get<Payment>('payments')
        .query(
          Q.where('invoice_id', invoiceId),
          Q.where('is_deleted', false),
        )
        .observe();
    },
    [invoiceId],
    [],
  );

  // ── Product names ──
  const productIds = useMemo(
    () => Array.from(new Set(lineItems.map((li) => li.productId))),
    [lineItems],
  );
  const [productNames, setProductNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (productIds.length === 0) return;
    let cancelled = false;
    (async () => {
      const map: Record<string, string> = {};
      for (let i = 0; i < productIds.length; i += 500) {
        const batch = productIds.slice(i, i + 500);
        const rows = await database
          .get<Product>('products')
          .query(Q.where('id', Q.oneOf(batch)))
          .fetch();
        for (const p of rows) map[p.id] = p.name;
      }
      if (!cancelled) setProductNames(map);
    })();
    return () => { cancelled = true; };
  }, [productIds]);

  // ── Customer ──
  const [customer, setCustomer] = useState<Customer | null>(null);
  useEffect(() => {
    if (!inv?.customerId) { setCustomer(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const c = await database.get<Customer>('customers').find(inv.customerId!);
        if (!cancelled) setCustomer(c);
      } catch {
        if (!cancelled) setCustomer(null);
      }
    })();
    return () => { cancelled = true; };
  }, [inv?.customerId]);

  if (!inv) {
    return (
      <div className="flex h-full items-center justify-center text-body text-fg-tertiary">
        Loading invoice…
      </div>
    );
  }

  const isCreditNote = inv.type === 'CREDIT_NOTE';
  const canReturn = inv.type === 'SALE' && inv.status === 'CONFIRMED' && can(BILLING_CREDIT_NOTE);
  const isVoided = VOIDED_INVOICE_STATUSES.includes(inv.status);
  const canVoid = inv.type === 'SALE' && !isVoided && can(BILLING_VOID);

  async function handleVoid() {
    if (!userId || !inv) return;
    const id = inv.id;
    const result = await voidInvoice(id, userId, voidReason);
    if (!result.ok) {
      setVoidError(result.message ?? 'Void failed.');
      return;
    }
    setVoidOpen(false);
    notifySuccess(`Invoice ${inv.invoiceNumber} voided.`);
    setHeaderInvoice(await database.get<Invoice>('invoices').find(id));
  }

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Top bar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => navigate('/invoices')}
          className="inline-flex items-center gap-2 rounded-md px-2 py-1.5 text-body text-fg-secondary hover:bg-bg-subtle hover:text-fg-primary transition-colors"
        >
          <ArrowLeft size={14} /> Back to invoices
        </button>
        <div className="flex flex-wrap items-center gap-2">
          {canReturn && (
            <Button variant="secondary" onClick={() => navigate(`/invoices/${inv.id}/return`)}>
              <RotateCcw size={14} className="mr-1.5" /> Return
            </Button>
          )}
          <Button variant="secondary" onClick={printWith(print80mm)} disabled={isPrinting}>
            <Printer size={14} className="mr-1.5" /> Receipt
          </Button>
          <Button variant="secondary" onClick={printWith(printSystem)} disabled={isPrinting} title="Print, or choose “Save as PDF” in the print dialog">
            <FileDown size={14} className="mr-1.5" /> A4 invoice
          </Button>
          {canVoid && (
            <Button variant="danger" onClick={() => setVoidOpen(true)}>
              <Ban size={14} className="mr-1.5" /> Void
            </Button>
          )}
        </div>
      </div>

      {/* Header card */}
      <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              {isCreditNote ? <Receipt size={16} className="text-warning-ink" /> : <FileText size={16} className="text-accent-ink" />}
              <span className="text-heading text-fg-primary">{inv.invoiceNumber}</span>
              <span className={cn(
                'rounded px-1.5 py-0.5 text-label uppercase',
                isCreditNote ? 'bg-warning/10 text-warning-ink' : 'bg-bg-subtle text-fg-secondary',
              )}>
                {TYPE_LABEL[inv.type] ?? inv.type}
              </span>
              {inv.isEmergency && (
                <span className="rounded px-1.5 py-0.5 text-label uppercase bg-warning/10 text-warning-ink">
                  Emergency
                </span>
              )}
              {isVoided && (
                <span className="rounded px-1.5 py-0.5 text-label uppercase bg-danger/10 text-danger-ink">
                  Void
                </span>
              )}
            </div>
            <div className="mt-1 text-label uppercase text-fg-tertiary tabular-nums">
              {new Date(inv.createdAt).toLocaleString()}
              {inv.referenceInvoiceId && (
                <span className="ml-2">· Against original</span>
              )}
            </div>
          </div>
          <div className="sm:text-right">
            <div className="text-label uppercase text-fg-tertiary">Total</div>
            <div className="text-display text-fg-primary tabular-nums">
              {formatCurrency(inv.totalAmount)}
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label="Subtotal" value={formatCurrency(inv.subtotal)} />
          <Stat label="Tax" value={formatCurrency(inv.totalTax)} />
          <Stat label="Paid" value={formatCurrency(inv.paidAmount)} accent="success" />
          <Stat
            label="Due"
            value={formatCurrency(inv.dueAmount)}
            accent={inv.dueAmount > 0 ? 'warning' : undefined}
          />
        </div>
      </div>

      {/* Body: lines + side */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-auto lg:grid-cols-[1fr_320px] lg:overflow-visible">
        {/* Lines */}
        <div className="overflow-auto rounded-lg border border-stroke-hairline/10 bg-bg-surface lg:min-h-0">
          <table className="w-full text-body">
            <thead className="sticky top-0 bg-bg-surface text-label uppercase text-fg-tertiary">
              <tr className="border-b border-stroke-hairline/10">
                <th className="px-4 py-2 text-left font-normal">Item</th>
                <th className="px-4 py-2 text-right font-normal">Qty</th>
                <th className="px-4 py-2 text-right font-normal">Unit price</th>
                <th className="px-4 py-2 text-right font-normal">Discount</th>
                <th className="px-4 py-2 text-right font-normal">Tax</th>
                <th className="px-4 py-2 text-right font-normal">Total</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-fg-tertiary">No items</td>
                </tr>
              ) : (
                lineItems.map((li) => {
                  const tax = li.cgstAmount + li.sgstAmount + li.igstAmount + li.cessAmount;
                  return (
                    <tr key={li.id} className="border-b border-stroke-hairline/10">
                      <td className="px-4 py-2.5">
                        <div className="text-fg-primary">{li.description?.trim() || productNames[li.productId] || '…'}</div>
                        {li.hsnCode && (
                          <div className="text-label uppercase text-fg-tertiary">HSN {li.hsnCode}</div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right text-fg-secondary tabular-nums">
                        {li.quantity} {li.unit}
                      </td>
                      <td className="px-4 py-2.5 text-right text-fg-secondary tabular-nums">
                        {formatCurrency(li.unitPrice)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-fg-secondary tabular-nums">
                        {li.discountAmount > 0 ? `− ${formatCurrency(li.discountAmount)}` : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right text-fg-secondary tabular-nums">
                        {tax > 0 ? formatCurrency(tax) : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right font-medium text-fg-primary tabular-nums">
                        {formatCurrency(li.totalAmount)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Side: customer + payments + notes */}
        <aside className="flex min-h-0 flex-col gap-4">
          <Card title="Customer">
            {customer ? (
              <>
                <div className="text-body text-fg-primary">{customer.name?.trim() || 'Unnamed'}</div>
                {customer.phone && (
                  <div className="text-label uppercase text-fg-tertiary tabular-nums">{customer.phone}</div>
                )}
                {customer.gstin && (
                  <div className="mt-1 text-label uppercase text-fg-tertiary">GSTIN {customer.gstin}</div>
                )}
              </>
            ) : (
              <div className="text-body text-fg-tertiary">Walk-in customer</div>
            )}
          </Card>

          <Card title="Payments">
            {payments.length === 0 ? (
              <div className="text-body text-fg-tertiary">No payments recorded.</div>
            ) : (
              <ul className="divide-y divide-stroke-hairline/10">
                {payments.map((p) => (
                  <li key={p.id} className="flex items-center justify-between py-2">
                    <div>
                      <div className="text-body text-fg-primary">{p.method}</div>
                      {p.transactionRef && (
                        <div className="text-label uppercase text-fg-tertiary truncate">
                          {p.transactionRef}
                        </div>
                      )}
                    </div>
                    <span className="text-body text-fg-primary tabular-nums">
                      {formatCurrency(p.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {inv.notes && (
            <Card title="Notes">
              <div className="whitespace-pre-wrap text-body text-fg-secondary">{inv.notes}</div>
            </Card>
          )}

          {inv.termsConditions?.trim() && (() => {
            // Quick Bill keeps dispatch details as labelled lines in Terms
            // (billing/invoiceExtras.ts); show them as fields, then the rest.
            const { details, freeTerms } = parseTerms(inv.termsConditions);
            const filled = DISPATCH_FIELDS.filter((f) => details[f.key]?.trim());
            return (
              <Card title={filled.length ? 'Dispatch and terms' : 'Terms'}>
                {filled.length > 0 && (
                  <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-body" data-testid="invoice-dispatch">
                    {filled.map((f) => (
                      <div key={f.key} className="contents">
                        <dt className="text-fg-tertiary">{f.label}</dt>
                        <dd className="text-fg-primary">{details[f.key]}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                {freeTerms && <div className={cn('whitespace-pre-wrap text-body text-fg-secondary', filled.length > 0 && 'mt-2')}>{freeTerms}</div>}
              </Card>
            );
          })()}

          {customer?.gstin && inv.type === 'SALE' && (
            <EInvoiceCard invoiceId={inv.id} />
          )}
        </aside>
      </div>
      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent title="Void invoice">
          <div className="space-y-4 p-5">
            <p className="text-body text-fg-secondary">
              Voiding restores item stock and removes unpaid customer due. Paid tenders remain in the audit trail.
            </p>
            <label className="block">
              <span className="mb-1 block text-label uppercase text-fg-tertiary">Reason</span>
              <Input value={voidReason} onChange={(e) => setVoidReason(e.target.value)} autoFocus />
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setVoidOpen(false)}>Cancel</Button>
              <Button variant="danger" onClick={() => void handleVoid()}>Void invoice</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: 'success' | 'warning' }) {
  const valClass =
    accent === 'success' ? 'text-success-ink' :
    accent === 'warning' ? 'text-warning-ink' :
    'text-fg-primary';
  return (
    <div>
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn('text-heading tabular-nums', valClass)}>{value}</div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{title}</div>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function EInvoiceCard({ invoiceId }: { invoiceId: string }) {
  const { local, remote, status, busy, error, generate } = useEInvoice(invoiceId);

  const irn = local?.irn ?? remote?.irn ?? null;
  const ackNumber = local?.ackNumber ?? remote?.ackNo ?? null;
  const ackDate = local?.ackDate ?? (remote?.ackDate ? new Date(remote.ackDate) : null);
  const signedQr = local?.signedQr ?? remote?.signedQr ?? null;
  const errorMessage = local?.errorMessage ?? remote?.errorMessage ?? error;

  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <ShieldCheck size={14} className="text-accent-ink" />
          <span className="text-label uppercase text-fg-tertiary">E-Invoice</span>
        </div>
        <StatusBadge status={status} />
      </div>

      {status === 'GENERATED' && irn && (
        <div className="mt-2 space-y-2">
          <div>
            <div className="text-label uppercase text-fg-tertiary">IRN</div>
            <div className="font-mono text-label text-fg-primary break-all">{irn}</div>
          </div>
          {ackNumber && (
            <div>
              <div className="text-label uppercase text-fg-tertiary">Ack number</div>
              <div className="font-mono text-body text-fg-primary tabular-nums">{ackNumber}</div>
            </div>
          )}
          {ackDate && (
            <div>
              <div className="text-label uppercase text-fg-tertiary">Ack date</div>
              <div className="text-body text-fg-secondary">{new Date(ackDate).toLocaleString()}</div>
            </div>
          )}
          {signedQr && signedQr.startsWith('data:') && (
            <img src={signedQr} alt="Signed QR" className="mt-2 h-32 w-32 rounded border border-stroke-hairline/10" />
          )}
        </div>
      )}

      {status === 'FAILED' && (
        <div className="mt-2 flex items-start gap-1.5 text-body text-warning-ink">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>{errorMessage || 'E-Invoice generation failed.'}</span>
        </div>
      )}

      {status !== 'GENERATED' && (
        <Button
          variant="secondary"
          className="mt-3 w-full"
          onClick={() => void generate()}
          disabled={busy}
        >
          {busy ? (
            <><Loader2 size={14} className="mr-1.5 animate-spin" /> {status === 'PENDING' ? 'Polling…' : 'Generating…'}</>
          ) : status === 'FAILED' ? (
            'Retry IRN'
          ) : (
            'Generate IRN'
          )}
        </Button>
      )}

      {error && status === 'GENERATED' && (
        <div className="mt-2 text-label text-warning-ink">{error}</div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    GENERATED: { label: 'Generated', cls: 'bg-success/10 text-success-ink' },
    PENDING:   { label: 'Pending',   cls: 'bg-warning/10 text-warning-ink' },
    FAILED:    { label: 'Failed',    cls: 'bg-warning/10 text-warning-ink' },
    NOT_GENERATED: { label: 'Not generated', cls: 'bg-bg-subtle text-fg-tertiary' },
  };
  const v = map[status] ?? map.NOT_GENERATED;
  return (
    <span className={cn('rounded px-1.5 py-0.5 text-label uppercase', v.cls)}>
      {v.label}
    </span>
  );
}
